import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import type { AxiosResponse } from "axios";
import type { ApiResponse } from "@/api/interface/apiResponse";
import { BaseService } from "@/api/services/baseService";
import type { SpecialZoneReservationOrderVoData } from "@/api/services/specialZoneReservationOrderVoTypes";
import type {
    Page,
    SpecialZoneReservationOrderCancelParams,
    SpecialZoneReservationOrderCountParams,
    SpecialZoneReservationOrderCountVo,
    SpecialZoneReservationOrderContractListParams,
    SpecialZoneReservationOrderGoodsList,
    SpecialZoneReservationOrderPagingParams,
    SpecialZoneReservationOrderPagingRow,
    SpecialZoneReservationOrderUpdateParams
} from "@zhihaoscm/entity";

/** 合同详情页预约单列表 — 分页数据（对应接口 `data` 字段，不含统一响应壳） */
export type SpecialZoneReservationOrderContractListPage = Page<SpecialZoneReservationOrderPagingRow>;

/** 合同详情页预约单列表 — 完整 HTTP 响应体（与 `@/api/interface/apiResponse` 一致，拦截器成功后会取出其中 `data`） */
export type SpecialZoneReservationOrderContractListApiResponse =
    ApiResponse<SpecialZoneReservationOrderContractListPage>;

/** 预约单 VO 详情完整响应（与后端字段一致；一般使用 {@link findSpecialZoneReservationOrderVoById} 解构出的 `data`） */
export interface FindSpecialZoneReservationOrderVoByIdRes extends ApiResponse<SpecialZoneReservationOrderVoData> {
    success: boolean;
}

/** OpenAPI 命名别名，等价于 {@link FindSpecialZoneReservationOrderVoByIdRes} */
export type FindVoById_1Res = FindSpecialZoneReservationOrderVoByIdRes;

/** `findVoById_1` 兼容载荷：部分环境返回 `account` 字段（与 `specialZoneAccount` 同义） */
type FindVoById_1Data = SpecialZoneReservationOrderVoData & {
    account?: SpecialZoneReservationOrderVoData["specialZoneAccount"];
};

export type SpecialZoneReservationOrderExportParams = Omit<SpecialZoneReservationOrderPagingParams, "page" | "size">;

export class SpecialZoneReservationOrderService extends BaseService<
    SpecialZoneReservationOrderPagingRow,
    SpecialZoneReservationOrderPagingRow,
    SpecialZoneReservationOrderPagingParams,
    unknown
> {}

/** 专区预约单分页列表 */
export async function paging_5(
    params: SpecialZoneReservationOrderPagingParams
): Promise<Page<SpecialZoneReservationOrderPagingRow>> {
    const { data } = await request.service.get<Page<SpecialZoneReservationOrderPagingRow>>(
        `/admin/special-zone-reservation-order/paging`,
        { params }
    );
    return data;
}

/** 专区预约单分页汇总 */
export async function findSpecialZoneReservationOrderCount(
    params: SpecialZoneReservationOrderCountParams
): Promise<SpecialZoneReservationOrderCountVo> {
    const { data } = await request.service.get<SpecialZoneReservationOrderCountVo>(
        `/admin/special-zone-reservation-order/find/count`,
        { params }
    );
    return data;
}

/** 预约单详情（与分页行结构一致） */
export async function getReservationOrderDetail(id: string): Promise<SpecialZoneReservationOrderPagingRow> {
    const { data } = await request.service.get<SpecialZoneReservationOrderPagingRow>(
        `/admin/special-zone-reservation-order/${id}`
    );
    return data;
}

/**
 * 查询预约单物料明细。
 * 供编辑预约单前按预约单维度回填物料计划量、单价与结算信息。
 */
export async function getReservationOrderGoods(id: string): Promise<SpecialZoneReservationOrderGoodsList> {
    const { data } = await request.service.get<SpecialZoneReservationOrderGoodsList>(
        `/admin/special-zone-reservation-order/${encodeURIComponent(id)}/goods`
    );
    return data;
}

/**
 * 预约单 VO 详情（含合同、船舶、客户、物料等聚合数据）
 *
 * `GET /admin/special-zone-reservation-order/vo/{id}`
 *
 * @param id 预约单 id
 */
export async function findSpecialZoneReservationOrderVoById(id: string): Promise<SpecialZoneReservationOrderVoData> {
    const { data } = await request.service.get<FindVoById_1Data>(
        `/admin/special-zone-reservation-order/vo/${encodeURIComponent(id)}`
    );
    if (data.specialZoneAccount == null && data.account != null) {
        return {
            ...data,
            specialZoneAccount: data.account
        };
    }
    return data;
}

/** OpenAPI 命名别名，等价于 {@link findSpecialZoneReservationOrderVoById} */
export const findVoById_1 = findSpecialZoneReservationOrderVoById;

/** 编辑预约单 */
export async function update_1(id: string, params: SpecialZoneReservationOrderUpdateParams): Promise<number> {
    const { data } = await request.service.put<number>(`/admin/special-zone-reservation-order/${id}`, params);
    return data;
}

/**
 * 审核预约单
 *
 * `PUT /admin/special-zone-reservation-order/audit/{id}`
 *
 * @param id 预约单 id（与列表 bookingNo / 主表 id 一致）
 * @returns 后端约定返回值（多为受影响行数等）
 */
export async function audit(id: string): Promise<number> {
    const { data } = await request.service.put<number>(
        `/admin/special-zone-reservation-order/audit/${encodeURIComponent(id)}`
    );
    return data;
}

/**
 * 取消预约单
 *
 * `PUT /admin/special-zone-reservation-order/cancel/{id}`
 *
 * @param id 预约单 id
 * @param params 可选请求体（如取消原因，与主表 cancelReason 对齐）
 */
export async function cancel(id: string, params?: SpecialZoneReservationOrderCancelParams): Promise<number> {
    const url = `/admin/special-zone-reservation-order/cancel/${encodeURIComponent(id)}`;
    const { data } =
        params != null ? await request.service.put<number>(url, params) : await request.service.put<number>(url);
    return data;
}

/**
 * 合同详情 — 预约单分页列表
 *
 * `GET /admin/special-zone-reservation-order/list`
 *
 * @param params.page 页码（从 1 开始）
 * @param params.size 每页条数
 * @param params.contractId 合同 id
 * @param params.sortKey 排序字段
 * @param params.sortOrder 排序方式
 */
export async function list(
    params: SpecialZoneReservationOrderContractListParams
): Promise<SpecialZoneReservationOrderContractListPage> {
    const { data } = await request.service.get<SpecialZoneReservationOrderContractListPage>(
        `/admin/special-zone-reservation-order/list`,
        { params }
    );
    return data;
}

function getExportFilename(contentDisposition?: string, fallback = "专区预约单.xlsx"): string {
    if (!contentDisposition) return fallback;
    const filename =
        contentDisposition
            .split("filename=")[1]
            ?.split(";")[0]
            ?.trim()
            .replace(/^["']|["']$/g, "") ?? "";
    if (!filename) return fallback;
    try {
        return decodeURIComponent(filename);
    } catch {
        return filename;
    }
}

function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/** 导出预约单列表 */
export async function exportSpecialZoneReservationOrderList(
    params: SpecialZoneReservationOrderExportParams,
    fallbackFilename = "专区预约单.xlsx"
): Promise<Blob> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>(
        `/admin/special-zone-reservation-order/export`,
        {
            params,
            responseType: "blob",
            returnRawResponse: true
        } as CustomAxiosRequestConfig
    );
    const filename = getExportFilename(response.headers["content-disposition"], fallbackFilename);
    downloadBlob(response.data, filename);
    return response.data;
}

export const specialZoneReservationOrderService = new SpecialZoneReservationOrderService(
    "/admin/special-zone-reservation-order",
    request.service
);
